// NPC vendor trade catalog for Nexus Forge — Phase 4.
//
// Data-driven, same shape as shared/recipes.ts: adding or removing an
// NPC-tradable item is a config change here, not a code change.
//
// Phase 4 scope (per DECISIONS 2026-04-18 "Phase 4 Step 5 NPC trades
// raws only"): NPC trades the four raw resources only. Crafted items
// (copper_wire, basic_circuit, …) are player-market-only. The NPC is
// baseline liquidity for materials; the player market is the point of
// the phase, so deliberately not undercutting it with NPC stock of
// finished goods.
//
// Per-item knobs live here as inline numeric literals — same precedent
// as shared/items.ts (item color, tier) and shared/recipes.ts (recipe
// structure). CLAUDE.md § "NPC vendor" explicitly directs:
//   "Per-item configuration … lives in a new shared/npc-items.ts
//    module. Tunable; follow the shared/recipes.ts pattern."
// The numeric literals here are data, not tuning knobs that move
// independently — bumping copper's basePrice means re-evaluating
// silicon's at the same time, so they belong together. Pricing-curve
// shape parameters (k, margin, restock interval/quantity) DO live in
// balance.npc per CLAUDE.md § "Balance values to add"; this file
// references them by name when the alarm/restock logic is built
// (Step 8).

import type { ItemId } from "./items.ts";

// One row in the NPC trade catalog. The pricing curve in market-npc.ts
// reads basePrice + maxInv together: at qty=maxInv the NPC quote sits
// at basePrice; as qty drops toward 0, the quote rises along
//   price = basePrice * (maxInv + k) / (qty + k)
// where k is balance.npc.priceCurveShape (default 10). Initial stock
// is what the NPC starts with at MarketDO bootstrap, before the first
// restock alarm fires.
export type NpcTradeItemConfig = {
  readonly itemId: ItemId;
  readonly basePrice: number;     // contracts:allow-literal — catalog data
  readonly maxInv: number;        // contracts:allow-literal — catalog data
  readonly initialStock: number;  // contracts:allow-literal — catalog data
};

// -----------------------------------------------------------------------
// Catalog
// -----------------------------------------------------------------------
//
// Pricing rationale (with k=10, margin=0.2):
//   copper / silicon (common raws):
//     basePrice 5, maxInv 100
//     → at qty=0:   price=55  (sell 66, buy 44)
//     → at qty=50:  price≈9   (sell ≈11, buy ≈7)
//     → at qty=100: price=5   (sell 6,  buy 4)
//   ferrite / polymer (rarer raws, smaller stockpile):
//     basePrice 10, maxInv 50
//     → at qty=0:   price=60  (sell 72, buy 48)
//     → at qty=25:  price≈17  (sell ≈21, buy ≈14)
//     → at qty=50:  price=10  (sell 12, buy 8)
//
// Sanity-checked against starting credits (100): a fresh player can
// buy 1–2 copper at scarcity or ~16 at saturation; selling 5 copper
// (one node's yield) returns 20–220 credits depending on NPC stock.
// Bootstrap path (gather copper → sell to NPC → buy what you need)
// works at both ends of the curve.
//
// initialStock = maxInv / 2 across the board: NPC starts mid-curve so
// players can interact immediately at the spec'd "fair" price, and
// the curve has equal headroom in both directions before the first
// restock alarm. See DECISIONS 2026-04-18 "Phase 4 Step 5 initial
// NPC stock = maxInv / 2".

// All numeric literals below are catalog data, not tuning knobs that
// move independently — see file header. Marked per-line so the
// magic-number checker (§4) lets them through.

const copper: NpcTradeItemConfig = {
  itemId: "copper",
  basePrice: 5,    // contracts:allow-literal — npc catalog data
  maxInv: 100,     // contracts:allow-literal — npc catalog data
  initialStock: 50, // contracts:allow-literal — npc catalog data
};

const silicon: NpcTradeItemConfig = {
  itemId: "silicon",
  basePrice: 5,    // contracts:allow-literal — npc catalog data
  maxInv: 100,     // contracts:allow-literal — npc catalog data
  initialStock: 50, // contracts:allow-literal — npc catalog data
};

const ferrite: NpcTradeItemConfig = {
  itemId: "ferrite",
  basePrice: 10,   // contracts:allow-literal — npc catalog data
  maxInv: 50,      // contracts:allow-literal — npc catalog data
  initialStock: 25, // contracts:allow-literal — npc catalog data
};

const polymer: NpcTradeItemConfig = {
  itemId: "polymer",
  basePrice: 10,   // contracts:allow-literal — npc catalog data
  maxInv: 50,      // contracts:allow-literal — npc catalog data
  initialStock: 25, // contracts:allow-literal — npc catalog data
};

// -----------------------------------------------------------------------
// Exports
// -----------------------------------------------------------------------

// Catalog map keyed by itemId for O(1) lookup at the RPC boundary
// (npcSell / npcBuy in market-npc.ts).
export const npcItemCatalog: Readonly<Partial<Record<ItemId, NpcTradeItemConfig>>> = {
  copper,
  silicon,
  ferrite,
  polymer,
};

// Ordered list for UI display in the NPC Shop tab. Plain raws first
// (copper / silicon), rarer raws after — matches the perceived value
// gradient and keeps the catalog stable for screenshot baselines.
export const npcItemList: readonly NpcTradeItemConfig[] = [
  copper,
  silicon,
  ferrite,
  polymer,
];

export function getNpcItem(itemId: string): NpcTradeItemConfig | undefined {
  return npcItemCatalog[itemId as ItemId];
}

// True if the NPC will trade this item in either direction. Used at
// the RPC boundary in market-npc.ts (npcSell / npcBuy) to reject
// requests for non-tradable items before any stock check.
export function isNpcTradable(itemId: string): boolean {
  return getNpcItem(itemId) !== undefined;
}

// Module-load integrity check. Same fail-fast philosophy as the
// balance.ts DDL-interpolation guard (DECISIONS 2026-04-18 "Phase 4
// Step 4 DDL-interpolation guard"): a future catalog edit that drops
// these invariants throws on import rather than silently producing
// div-by-zero in the price curve (maxInv=0) or negative stock at
// boot (initialStock<0). Cheaper than catching it at runtime via a
// chaos test or, worse, a player-visible NaN.
for (const cfg of npcItemList) {
  if (!Number.isInteger(cfg.basePrice) || cfg.basePrice <= 0) {
    throw new Error(`npc-items: ${cfg.itemId} basePrice must be a positive integer; got ${cfg.basePrice}`);
  }
  if (!Number.isInteger(cfg.maxInv) || cfg.maxInv <= 0) {
    throw new Error(`npc-items: ${cfg.itemId} maxInv must be a positive integer; got ${cfg.maxInv}`);
  }
  if (!Number.isInteger(cfg.initialStock) || cfg.initialStock < 0) {
    throw new Error(`npc-items: ${cfg.itemId} initialStock must be a non-negative integer; got ${cfg.initialStock}`);
  }
  if (cfg.initialStock > cfg.maxInv) {
    throw new Error(
      `npc-items: ${cfg.itemId} initialStock (${cfg.initialStock}) must be <= maxInv (${cfg.maxInv})`,
    );
  }
}
